import { ComponentProps, HTMLAttributes, ReactNode, forwardRef, useRef } from 'react';
import { mergeProps, useButton, useFocusRing, useId, useLabel } from 'react-aria';
import { useListState, useOverlayTriggerState } from 'react-stately';

import composeRefs from '@seznam/compose-react-refs';

import defaultEntryFilter from './defaultEntryFilter';
import defaultEntryRenderer from './defaultEntryRenderer';
import EntryList from './EntryList';
import useEntries from './hooks/useEntries';
import useSearch from './hooks/useSearch';
import SearchEntriesInput from './SearchEntriesInput';
import TriggerButton from './TriggerButton';
import { MultiSelection, SelectEntry, SelectEntryID } from './types';

import { Spinner } from '~/components/Icons/Spinner';
import Chip from '~/components/scales/Chip/Chip';
import ClearButton from '~/components/scales/ClearButton/ClearButton';
import ClearIconButton from '~/components/scales/ClearIconButton/ClearIconButton';
import Hint from '~/components/scales/Hint/Hint';
import useHasClearButton from '~/components/scales/hooks/useHasClearButton';
import Icon from '~/components/scales/Icon/Icon';
import Popover from '~/components/scales/Popover/Popover';
import ScrollContainer from '~/components/scales/ScrollContainer';
import Tooltip from '~/components/scales/Tooltip/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';
import useIsElementTruncated from '~/hooks/useIsElementTruncated';
import useStack from '~/hooks/useStack';
import { StrictUnion, isNonNullable } from '~/utilities/types';

type BaseProps = {
  'aria-label'?: string;
  'data-cy'?: string;
  defaultValue?: MultiSelection;
  endAdornment?: ReactNode;
  entries?: SelectEntry[];
  errorMessage?: string;
  hint?: ComponentProps<typeof Hint>['content'];
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  label?: string;
  onFetchChildren?: ComponentProps<typeof EntryList>['onFetchChildren'];
  onSearchChange?: (searchString: string) => void;
  onViewChildren?: ComponentProps<typeof EntryList>['onViewChildren'];
  placeholder?: string;
  startAdornment?: ReactNode;
  value?: MultiSelection;
};

type AllSelectableProps = BaseProps & {
  /** @alpha Select-All is alpha - provide feedback please :-) */
  isAllSelectable: true | boolean;
  onChange?: (value: SelectEntryID[]) => void;
};

type NotAllSelectableProps = BaseProps & {
  isAllSelectable?: false;
  onChange?: (value: SelectEntryID[]) => void;
};

type Props = StrictUnion<AllSelectableProps | NotAllSelectableProps>;

export default forwardRef<HTMLButtonElement, Props>(function MultiSelect(props, forwardedRef) {
  const search = useSearch({ onChange: props.onSearchChange });

  // This is a stack of SelectEntryIDs that holds our current position in a tree of entries. This
  // is used when we have multilevel entries such that an entry has child entries, which
  // have child entries, which have child entries, etc.
  //
  // The most common use of this is multilevel categorizations. In a case like that, a stack may
  // look like ['030000', '030600', '030620'] which would have us showing the entries underneath
  // "Schedules for Concrete Reinforcing" - 030620.13 and 030620.16
  const currentRootStack = useStack<SelectEntryID>();

  // Pull the entries we're going to feed into React Aria.
  const { disabledKeys, entries } = useEntries({ entries: props.entries ?? [] });

  const listStateProps: Parameters<typeof useListState<SelectEntry>>[0] = {
    children: defaultEntryRenderer,
    defaultSelectedKeys: props.defaultValue,
    disabledKeys,
    items: entries,
    onSelectionChange: (keys) => {
      /* We are not using the possible 'all' string value for the keys prop,
      because our select all functionality selects a subset of entries, not
      every possible entry. This check in to narrow our keys arg to a string[] type
       */
      if (typeof keys === 'string') {
        return;
      }

      const stringKeys: SelectEntryID[] = [...keys.values()].map((v) => `${v}`);
      props.onChange?.(stringKeys);
    },
    selectedKeys: props.value,
    selectionMode: 'multiple',
  };

  const state = useListState<SelectEntry>(listStateProps);

  const ref = useRef<HTMLButtonElement>(null); // This gets passed into RA
  const composedRef = composeRefs(ref, forwardedRef); // This gets applied.

  const inputId = useId();
  const valueId = useId();
  const errorMessageId = useId();

  const { fieldProps, labelProps } = useLabel({
    'aria-label': props['aria-label'],
    'aria-labelledby': valueId,
    'aria-describedby': errorMessageId,
    label: props.label,
    id: inputId,
  });

  const popoverState = useOverlayTriggerState({
    onOpenChange: () => {
      search.onChange('');
      currentRootStack.clear();
    },
  });

  const { buttonProps } = useButton(
    {
      'aria-expanded': popoverState.isOpen,
      'aria-describedby': props.errorMessage ? errorMessageId : undefined,
      'aria-haspopup': 'listbox',
      id: inputId,
      isDisabled: props.isDisabled || !entries.length,
      onKeyDown: (e) => {
        if (e.key === 'ArrowDown') {
          popoverState.open();
        }
      },
      onPress: popoverState.toggle,
    },
    ref
  );
  const { focusProps, isFocusVisible } = useFocusRing();
  const triggerButtonProps = mergeProps(buttonProps, focusProps, fieldProps);

  const { hasTextClearButton, hasInlineClearButton } = useHasClearButton({
    label: props.label,
    isClearable:
      props.isClearable &&
      (state.selectionManager.isSelectAll || state.selectionManager.selectedKeys.size !== 0),
    isDisabled: props.isDisabled,
  });
  const handleClear = () => {
    state.selectionManager.setSelectedKeys([]);
    ref.current?.focus();
  };

  const selectedItems = [...state.selectionManager.selectedKeys]
    .map((key) => state.collection.getItem(key)?.value)
    .filter(isNonNullable);

  validateDataCy(props['data-cy'], 'multi-select');

  return (
    <div className="relative flex w-full flex-col gap-0.5">
      {props.label && (
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1">
            <label {...labelProps} className="text-type-primary type-label">
              {props.label}
            </label>
            {props.hint && <Hint content={props.hint} />}
          </div>
          {hasTextClearButton && <ClearButton onClick={handleClear} />}
        </div>
      )}
      <TriggerButton
        buttonProps={triggerButtonProps}
        data-cy={props['data-cy'] ?? 'multi-select'}
        isDisabled={props.isDisabled}
        isFocusVisible={isFocusVisible}
        isInvalid={Boolean(props.errorMessage)}
        triggerRef={composedRef}
        type="default"
      >
        {props.startAdornment}
        <SelectedValues
          entries={props.entries}
          isDisabled={props.isDisabled}
          placeholder={props.placeholder}
          selectedItems={selectedItems}
          valueProps={{ id: valueId }}
        />
        {hasInlineClearButton && <ClearIconButton onClick={handleClear} />}
        {props.endAdornment}
        {props.isLoading ? <Spinner /> : <Icon aria-hidden="true" name="arrow_drop_down" />}
      </TriggerButton>
      {props.errorMessage && (
        <div className="cursor-default text-type-error type-label" id={errorMessageId}>
          {props.errorMessage}
        </div>
      )}
      {/*
      TODO: A hidden select would give us autofill support, but that doesn't seem so critical rn.
      <HiddenSelect
        state={{ ...state, isFocused }}
        {...fieldProps}
        triggerRef={ref}
        label={props.label}
        name={props.name}
      />
      */}
      {popoverState.isOpen && (
        <Popover className="flex flex-col" isWidthOfTrigger state={popoverState} triggerRef={ref}>
          {props.isSearchable && (
            <SearchEntriesInput
              onChange={search.onChange}
              onEscape={() => popoverState.setOpen(false)}
              value={search.value}
            />
          )}
          <ScrollContainer direction="vertical">
            <EntryList
              isAllSelectable={props.isAllSelectable}
              isLoading={props.isLoading}
              isMultiSelect
              menuProps={{
                'aria-labelledby': labelProps.id,
                autoFocus: false, // let the FilterTextInput grab focus
              }}
              onFetchChildren={props.onFetchChildren}
              onFilterEntry={(entry) =>
                defaultEntryFilter(entry, search.value, currentRootStack.top)
              }
              // Don't show the option to see children if we're searching for entries.
              // The UX gets confusing otherwise because its unclear whether we should be
              // search all the entries or just the children.
              onViewChildren={search.value ? undefined : currentRootStack.push}
              onViewParent={currentRootStack.pop}
              // Don't show the parent if we're searching for entries
              parentEntryID={search.value ? undefined : currentRootStack.top}
              state={state}
            />
          </ScrollContainer>
        </Popover>
      )}
    </div>
  );
});

const SelectedValues = (props: {
  isDisabled?: boolean;
  selectedItems: SelectEntry[];
  placeholder?: string;
  valueProps: HTMLAttributes<HTMLDivElement>;
  entries?: SelectEntry[];
}) => {
  const { ref, isTruncated } = useIsElementTruncated<HTMLDivElement>();

  let contents: ReactNode = props.placeholder ?? 'Select an option...';
  const values = getDisplayedValues(props.selectedItems, props.entries);
  if (values.length) {
    contents = values.map((entry) => (
      <Chip
        key={entry.id}
        backgroundColor={props.isDisabled ? 'var(--colors-background-primary)' : undefined}
        text={entry.label}
      />
    ));
  }

  return (
    <Tooltip
      content={
        values.length ? (
          <ul>
            {values.map((entry) => (
              <li key={entry.id}>•&nbsp;{entry.label}</li>
            ))}
          </ul>
        ) : undefined
      }
      isDisabled={!isTruncated}
      placement="bottom"
    >
      <div
        {...props.valueProps}
        ref={ref}
        className={[
          'relative mr-auto flex h-full w-full items-center gap-1 overflow-auto truncate',
          props.selectedItems.length ? 'text-type-primary' : 'text-type-inactive',
        ].join(' ')}
      >
        {contents}
        {/* The following is essentially the ScrollShadow we use in a ScrollContainer.
            Note how in this version we don't have an animation-range - we simply flip between visible and invisible. */}
        <div
          className="pointer-events-none sticky -right-1 -ml-8 h-full w-8 shrink-0 text-right text-background-primary opacity-0"
          style={{
            animation: 'auto linear to-opaque both',
            // @ts-expect-error CSSProperties hasn't been updated with animation-timeline
            animationTimeline: 'scroll(x)',
            animationDirection: 'reverse',

            /* We're being cute with the definition here because we want the gradient to
            move from the background color to transparent. There's no real mechanism for
            getting the current /background/ color, only the text color, so we set this div's
            text color to the themed background color (in the `className` on this div) so that we
            can use it in this gradient definition. As a result, we're theme/dark-mode aware. */
            backgroundImage:
              'linear-gradient(to left, currentColor 0%, currentColor 25%, transparent 100%)',
          }}
        />
      </div>
    </Tooltip>
  );
};

const getDisplayedValues = (
  selectedItems: SelectEntry[],
  entries?: SelectEntry[]
): { id: UUID; label: string }[] => {
  if (!selectedItems.length) {
    return [];
  }

  const sections = entries?.filter((item) => item.isSection);
  if (!sections?.length) {
    return selectedItems;
  }

  const selectedIds = new Set(selectedItems.map((item) => item.id));

  // If all entries in all sections are selected and there's more than one section, show "All"
  if (
    sections.length > 1 &&
    sections.every((section) => section.entries?.every((entry) => selectedIds.has(entry.id)))
  ) {
    return [{ label: 'All', id: 'all' }];
  }

  return [
    ...sections.flatMap((section) => {
      const entries = section.entries || [];
      // return section if all its entries are selected, otherwise return its selected entries
      return entries.every((entry) => selectedIds.has(entry.id))
        ? [section]
        : entries.filter((entry) => selectedIds.has(entry.id));
    }),
    // Add any selected items that aren't in sections
    ...selectedItems.filter(
      (item) => !sections.some((section) => section.entries?.some((entry) => entry.id === item.id))
    ),
  ];
};
